import { z } from 'zod';

/**
 * Planilhas de recebimento.
 *
 * A Apen envia ao longo do mes as planilhas que sustentam a emissao das notas
 * fiscais. O fluxo tem uma regra que da forma ao resto: uma vez enviada, a
 * planilha nao pode ser trocada nem apagada por quem enviou. Ela vira
 * evidencia de qual documento gerou quais notas — e evidencia que o autor pode
 * substituir sozinho nao serve de evidencia.
 *
 * Para corrigir, quem enviou pede, um administrador libera, e so entao o
 * arquivo pode ser substituido. A versao anterior fica guardada.
 */

export const sheetStatus = z.enum(['enviada', 'correcao_pedida', 'correcao_liberada']);
export type SheetStatus = z.infer<typeof sheetStatus>;

export const SHEET_STATUS_LABEL: Record<SheetStatus, string> = {
  enviada: 'Enviada',
  correcao_pedida: 'Correção pedida',
  correcao_liberada: 'Correção liberada',
};

/** Formatos que o leitor de planilha do escritorio abre. */
export const SHEET_MIME_TYPES: Record<string, string> = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.oasis.opendocument.spreadsheet': '.ods',
  'text/csv': '.csv',
};

export const SHEET_MAX_BYTES = 10 * 1024 * 1024;

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Informe a data no formato aaaa-mm-dd.');

export const criarPlanilhaRequest = z
  .object({
    period_start: isoDate,
    period_end: isoDate,
    notes: z.string().trim().max(255).optional(),
  })
  .refine((body) => body.period_start <= body.period_end, {
    message: 'A data inicial nao pode ser depois da final.',
    path: ['period_end'],
  });

export const pedirCorrecaoRequest = z.object({
  reason: z
    .string()
    .trim()
    .min(5, 'Explique o que precisa ser corrigido.')
    .max(500, 'Use no maximo 500 caracteres.'),
});

export const planilhaArquivo = z.object({
  id: z.number().int().positive(),
  version: z.number().int().positive(),
  file_name: z.string(),
  file_size: z.number().int().nonnegative(),
  uploaded_by_name: z.string().nullable(),
  uploaded_at: z.string(),
});
export type PlanilhaArquivo = z.infer<typeof planilhaArquivo>;

export const planilha = z.object({
  id: z.number().int().positive(),
  competencia: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  notes: z.string().nullable(),
  status: sheetStatus,
  correction_reason: z.string().nullable(),
  created_by_name: z.string().nullable(),
  created_at: z.string(),
  /** Da mais recente para a mais antiga. A primeira e a que vale. */
  arquivos: z.array(planilhaArquivo),
});
export type Planilha = z.infer<typeof planilha>;

/** `2026-08-01` + `2026-08-30` vira `01 a 30 de agosto de 2026`. */
export function intervaloPorExtenso(inicio: string, fim: string): string {
  const MESES = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
  ];
  const [ai, mi, di] = inicio.split('-');
  const [af, mf, df] = fim.split('-');

  const nomeI = MESES[Number(mi) - 1] ?? mi;
  const nomeF = MESES[Number(mf) - 1] ?? mf;

  if (ai === af && mi === mf) return `${di} a ${df} de ${nomeI} de ${ai}`;
  if (ai === af) return `${di} de ${nomeI} a ${df} de ${nomeF} de ${ai}`;
  return `${di}/${mi}/${ai} a ${df}/${mf}/${af}`;
}

export function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}
